import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import Modal from "../../../../Components/v2/Modal";
import FormDate from "../../../../Components/v2/Form/FormDate";
import TableSkeleton from "../../../../Components/TableSkeleton";
import ErrorStateTable from "../../../../Components/ErrorStateTable";
import CapitalMatrixTable from "../../../../Components/v2/Accounting/CapitalMatrixTable";

import { cancelAPIRequest, getAccountingFileName } from "../../../../Utils";
import {
  clearExportCapitalStatement,
  requestCapitalStatement,
  requestExportCapitalStatement,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { EXPORTS } from "../../../../Constants/accountingReport";
import {
  wibEndOfDay,
  wibMonthRange,
  wibToday,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingCapitalStatement = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();

  const [date, setDate] = useState(wibToday);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    capitalAccounts: storedAccounts,
    capitalLoading,
    capitalError,
    capitalMatrix: storedMatrix,
    capitalExportLoading,
    capitalExportError,
    capitalExportFile,
  } = useAccountingState();

  // With the date cleared nothing is requested, so the store still holds the
  // previous date's report. Show nothing rather than present it under an empty
  // picker.
  const capitalAccounts = date ? storedAccounts : [];
  const capitalMatrix = date ? storedMatrix : null;

  const accounts = capitalAccounts ?? [];
  // The matrix is the face of the statement and stands on its own. It used to
  // be gated on the equity-account detail below, so a young business — real
  // profit for the period, no equity account moved yet — saw the whole report
  // replaced by "no data" even though the PDF export showed the profit.
  // CapitalMatrixTable renders nothing without rows, so this only asks whether
  // there is a statement to show.
  const hasMatrix = (capitalMatrix?.rows?.length ?? 0) > 0;

  // --- Effects ---
  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearExportCapitalStatement(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.mainBusiness?.id, date]);

  useEffect(() => {
    if (capitalExportFile?.key) {
      downloadFile(capitalExportFile.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capitalExportFile]);

  useEffect(() => {
    if (capitalExportError) {
      setShowExportError(true);
    }
  }, [capitalExportError]);

  // --- Handlers ---
  const onLoad = () => {
    if (request) cancelAPIRequest(request);
    if (user.mainBusiness?.id && date) {
      requestCapitalStatement(accountingDispatch, {
        businessID: user.mainBusiness.id,
        date: wibEndOfDay(date),
      });
    }
  };

  const handleExport = useCallback(
    (exportType) => {
      if (user.mainBusiness?.id) {
        requestExportCapitalStatement(accountingDispatch, {
          businessID: user.mainBusiness.id,
          date: wibEndOfDay(date),
          exportType,
        });
      }
    },
    [accountingDispatch, date, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      downloadBlob(result.body, getAccountingFileName(key));
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportCapitalStatement(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  const dateParams = date
    ? `&startDate=${wibMonthRange(date).startDate}&endDate=${wibMonthRange(date).endDate}`
    : "";

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="Capital Statement"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: capitalExportLoading,
          }}
        />
        {/* As-of report: no startDate, the range runs from the start of the book. */}
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          endDate={date}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Date filter */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDate
              selected={date}
              onChange={(val) => setDate(val)}
              disabled={capitalLoading}
              onClear={() => setDate(null)}
            />
          </div>

          {/* Statement of changes in equity matrix (PSAK 1 / IAS 1 face) */}
          {!capitalLoading && hasMatrix && (
            <div className="mb-6">
              <CapitalMatrixTable matrix={capitalMatrix} />
            </div>
          )}

          {/* Per-account equity detail. Labelled even when empty: under the
              statement above, an unlabelled "no data" table reads as though the
              whole report were empty. */}
          {!capitalLoading && (
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              {t("capitalStatement.accountDetail", "Rincian Akun Ekuitas")}
            </h3>
          )}
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {accounts.length > 1 ? t("Accounts") : t("Account")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                          {t("capitalStatement.beginningBalance", "Saldo Awal")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                          {t("capitalStatement.change", "Perubahan")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                          {t("capitalStatement.endingBalance", "Saldo Akhir")}
                        </th>
                      </tr>
                    </thead>
                    {capitalLoading ? (
                      <TableSkeleton rows={4} columns={4} />
                    ) : (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {accounts.length > 0 ? (
                          <>
                            {/* Capital accounts movement */}
                            {accounts.map((item, index) => {
                              // A deleted account leaves a null relation behind.
                              const { id, code, name } = item.account || {};
                              return (
                                <tr key={index}>
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    {code ? (
                                      <a
                                        href={`/v2/accounting/transactions?accountId=${id}${dateParams}`}
                                        className="text-sm text-blue-600 hover:underline"
                                      >
                                        {`${code} - ${name}`}
                                      </a>
                                    ) : (
                                      <span className="text-gray-700">
                                        {name}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                    <NumericFormat
                                      displayType="text"
                                      value={item.beginning}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      prefix="Rp"
                                    />
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                    <NumericFormat
                                      displayType="text"
                                      value={item.current}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      prefix="Rp"
                                    />
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                    <NumericFormat
                                      displayType="text"
                                      value={item.ending}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      prefix="Rp"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        ) : (
                          <ErrorStateTable
                            otherError={capitalError}
                            onRetry={onLoad}
                          />
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download progress modal */}
        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) downloadRef.current.cancel();
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {/* Error modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {capitalExportError && (
          <ErrorDialog
            open={showExportError}
            error={capitalExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportCapitalStatement(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingCapitalStatement;
